import React, { useEffect, useState, Fragment, useMemo } from 'react';
import TimeRowGroup from '../../types/TimePeriodGroupM';
import '../../Styles/TimePeriod.scss';
import { TimeGridMode } from '../../types/TimeGridMode';
import { SortTimeRows, GetTotalFromTimeRowInDayArray } from '../Utils';
import { PeriodDay } from '../../types/PeriodDay';
import { ScrollablePane, Stack, Text, FontIcon, IconButton } from '@fluentui/react';
import { ValidateResponse } from '../../Validation/Validator';
import TimePeriodGroupWeek from './TimePeriodGroupWeek';
import { DisplayCell } from './DisplayCell';
import { useGridContext } from '../Contexts/GridContext/context';
import { useConfigValuesState } from '../../hooks/useConfigValue';
import { UserRegistrationPeriod, UserReportingPeriodState, WorkType } from '../../api/generated/data-contracts';
import { useTranslation } from 'react-i18next';
import { getPeriodDays, getPeriodDaysEnabled } from '../../helpers/periodDays';

interface IProps {
    times: UserRegistrationPeriod[];
    onCellWeekUpdated: (
        time: UserRegistrationPeriod,
        periodDays: PeriodDay[],
        te: any,
        cellValue: number,
        allCellsValue: number,
        comment: string,
        flagged: boolean,
        prevVal: number,
    ) => Promise<string[]>;
    mode: TimeGridMode;
    pinTime: Function;
    deleteTime: Function;
    today: Date;
    submitted: boolean;
    reportPeriodId: string;
    search: number;
    periodIsClosed: boolean;
    startDate: Date;
    endDate: Date;
    deadlineDate: Date;
    timesheetstate: UserReportingPeriodState;
    timesheetmessage: string;
    updateWorkType: (row: UserRegistrationPeriod, workType: WorkType, changeCompleted: Function) => void;
    validationErrors: ValidateResponse[];
    copyFromLastWeek: () => void;
    onRemainingWorkUpdate?: (assignmentId: string, newRemainingWork: number) => void;
}

export const TimePeriodWeekOverview: React.FC<IProps> = props => {
    const {
        initialData: {
            configuration: { showPlanned, gridInputType, workTypesEnabled, weekConfig },
        },
    } = useConfigValuesState();

    const { t } = useTranslation('timesheet');
    const [splitBarMouseDown, setSplitBarMouseDown] = useState<boolean>(false);
    const [splitBarStartPos, setSplitBarStartPos] = useState({ mouseStart: 0, barLastPos: 0 });
    const [timeRowGroups, setTimeRowGroups] = useState<TimeRowGroup[]>([]);
    const [expanded, setExpanded] = useState<boolean>(true);
    const [groupiconrotation, setGroupiconrotation] = useState<string>('rotate(0deg)');

    let timesheetIsReadonly = false;

    if (props.submitted) timesheetIsReadonly = true;
    if (props.periodIsClosed) timesheetIsReadonly = true;
    if (props.timesheetstate === 'Rejected') timesheetIsReadonly = false;

    const [totalarray, setTotalarray] = useState<any>(null);

    const [searchActivate, setSearchActivate] = React.useState<number>(0);

    const [perioddays, setPerioddays] = useState<PeriodDay[]>([]);
    const { splitBarPos, setSplitBarPos, plannedActive, setPlannedActive, actualActive, setActualActive, remainingActive, setRemainingActive } =
        useGridContext();
    const [splitterbarPosition, setSplitterbarPosition] = useState<number>(Number(splitBarPos));

    useEffect(() => {
        if (perioddays.length === 0) {
            return;
        }
        const sortedTasks: UserRegistrationPeriod[] = SortTimeRows(props.times);
        let timeTaskRowsCount = 0;
        const groups: TimeRowGroup[] = [];
        let group: TimeRowGroup = null;

        for (const element of sortedTasks) {
            const groupExists = groups.find(_ => _.GroupId === element.projectId);

            if (!groupExists) {
                const showProjectName = element.projectName + `${element.projectNumber ? ` (${element.projectNumber})` : ''}`;

                group = new TimeRowGroup(element.projectId, showProjectName, element.projectSource, [], true);
                groups.push(group);
                group.TimeRows.push(element);
            } else {
                const existingGroup = groups.find(_ => _.GroupId === element.projectId);
                existingGroup.TimeRows.push(element);
            }
            timeTaskRowsCount++;
        }

        setTimeRowGroups(prevState => {
            if (prevState.length > 0) {
                const previousGroups = prevState.filter(group => groups.some(ps => ps.GroupId === group.GroupId));
                const newGroups = groups.map(newGroup => {
                    const existingGroup = previousGroups.find(previousGroup => previousGroup.GroupId === newGroup.GroupId);
                    if (!existingGroup) {
                        return newGroup;
                    }
                    if (newGroup.TimeRows.length !== existingGroup.TimeRows.length) {
                        return { ...newGroup, Expanded: true };
                    }
                    return { ...newGroup, Expanded: existingGroup.Expanded };
                });

                return newGroups;
            } else {
                // Here you can set the max rows before starting as collapsed
                if (groups.length + timeTaskRowsCount > 20) {
                    setExpanded(false);
                }
                return groups;
            }
        });

        const mtotalarray = {};

        for (let pi = 0; pi < perioddays.length; pi++) {
            const pdate: number = perioddays[pi].id;
            const oldval = mtotalarray[pdate] || 0;
            mtotalarray[pdate] = oldval + GetTotalFromTimeRowInDayArray(sortedTasks, perioddays[pi].date);
        }
        setTotalarray(mtotalarray);
    }, [props.times, perioddays]);

    const workingDays = useMemo(() => {
        return getPeriodDaysEnabled(perioddays, t, weekConfig);
    }, [perioddays, t, weekConfig]);

    useEffect(() => {
        const periodDaysArr = getPeriodDays(props.startDate, props.endDate, t);
        setPerioddays(periodDaysArr);
    }, [props.startDate, props.endDate, t, weekConfig]);

    useEffect(() => {
        if (props.search > 0) {
            setSearchActivate(props.search);
            setExpanded(false);
        }
    }, [props.search]);

    useEffect(() => {
        setTimeRowGroups(groups => {
            return groups.map(g => ({ ...g, Expanded: expanded }));
        });
    }, [expanded]);

    const splitbarMouseDownEvent = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();
        if (!splitBarMouseDown) {
            setSplitBarMouseDown(true);
            setSplitBarStartPos({ mouseStart: event.pageX, barLastPos: splitBarPos });
            setSplitterbarPosition(Number(splitBarPos));
        }
    };

    const splitbarGridMouseUpEvent = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setSplitBarMouseDown(false);
        setSplitBarPos(splitterbarPosition);
    };

    const splitbarGridMouseMoveEvent = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (splitBarMouseDown) {
            const xmove = splitBarStartPos.barLastPos - (splitBarStartPos.mouseStart - event.pageX);
            if (xmove > 75 && xmove < 2000 && xmove !== splitterbarPosition && xmove % 2 === 0) {
                setSplitterbarPosition(xmove);
            }
        }
    };

    const groupGollapsedExpanded = (ev: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>): void => {
        if (expanded) {
            setGroupiconrotation('rotate(-90deg)');
        } else {
            setGroupiconrotation('rotate(0deg)');
        }
        setExpanded(e => !e);
    };

    const PinMyTime = (time: UserRegistrationPeriod, pinned: boolean) => {
        props.pinTime(time, pinned, props.mode);
    };

    const DeleteMyTime = (time: UserRegistrationPeriod) => {
        props.deleteTime(time, perioddays);
    };

    return (
        <div className="timeperiod" onMouseUp={splitbarGridMouseUpEvent} onMouseMove={splitbarGridMouseMoveEvent}>
            <ScrollablePane>
                <div className="tableWrapper">
                    <table>
                        <thead>
                            <tr className={`noselect topHeadRow`}>
                                <th className="headerContainer noselect">
                                    <Stack horizontal className="headerCell" styles={{ root: { flexWrap: 'nowrap' } }}>
                                        <div className="expander" onClick={groupGollapsedExpanded}>
                                            <div className="expanderInner">
                                                <FontIcon
                                                    iconName="ChevronDown"
                                                    style={{
                                                        transform: groupiconrotation,
                                                        transition: 'transform .2s ease 0s',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <Text className="groupName textOverflow">{t('table.all')}</Text>
                                        <IconButton
                                            menuIconProps={{ iconName: 'More' }}
                                            menuProps={
                                                showPlanned && gridInputType !== 'percent'
                                                    ? {
                                                          items: [
                                                              {
                                                                  key: 'copyFromLastWeek',
                                                                  text: t('table.status.copyFromLastWeek'),
                                                                  iconProps: { iconName: 'Copy' },
                                                                  disabled: timesheetIsReadonly,
                                                                  onClick: () => props.copyFromLastWeek(),
                                                              },
                                                              {
                                                                  key: 'plannedActive',
                                                                  text: t('table.status.plannedActive'),
                                                                  iconProps: { iconName: plannedActive ? 'CheckboxCompositeReversed' : 'Checkbox' },
                                                                  onClick: () => setPlannedActive(!plannedActive),
                                                              },
                                                              {
                                                                  key: 'actualActive',
                                                                  text: t('table.status.actualActive'),
                                                                  iconProps: { iconName: actualActive ? 'CheckboxCompositeReversed' : 'Checkbox' },
                                                                  onClick: () => setActualActive(!actualActive),
                                                              },
                                                              {
                                                                  key: 'remainingActive',
                                                                  text: t('table.status.remainingActive'),
                                                                  iconProps: { iconName: remainingActive ? 'CheckboxCompositeReversed' : 'Checkbox' },
                                                                  onClick: () => setRemainingActive(!remainingActive),
                                                              },
                                                          ],

                                                          // By default, the menu will be focused when it opens. Uncomment the next line to prevent this.
                                                          // shouldFocusOnMount: false
                                                      }
                                                    : {
                                                          items: [
                                                              {
                                                                  key: 'copyFromLastWeek',
                                                                  text: t('table.status.copyFromLastWeek'),
                                                                  iconProps: { iconName: 'Copy' },
                                                                  disabled: timesheetIsReadonly,
                                                                  onClick: () => props.copyFromLastWeek(),
                                                              },
                                                          ],

                                                          // By default, the menu will be focused when it opens. Uncomment the next line to prevent this.
                                                          // shouldFocusOnMount: false
                                                      }
                                            }
                                        ></IconButton>
                                        {workTypesEnabled && (
                                            <div className="adjustwidthsplitter" style={{ position: 'absolute', right: 0, height: '100%' }}>
                                                <div onMouseDown={splitbarMouseDownEvent}>
                                                    <div className="splitbar"></div>
                                                </div>
                                            </div>
                                        )}
                                    </Stack>
                                </th>
                                {workTypesEnabled && (
                                    <th className="headerContainer noselect">
                                        <Stack
                                            horizontal
                                            className="headerCell"
                                            verticalAlign="center"
                                            horizontalAlign="center"
                                            styles={{ root: { flexWrap: 'nowrap', height: '100%', position: 'relative' } }}
                                        >
                                            <Text as="div" className="headerCell workType" style={{ flexGrow: 1 }}>
                                                {t('table.workType')}
                                            </Text>
                                            <div className="adjustwidthsplitter" style={{ position: 'absolute', right: 0, height: '100%' }}>
                                                <div onMouseDown={splitbarMouseDownEvent}>
                                                    <div className="splitbar"></div>
                                                </div>
                                            </div>
                                        </Stack>
                                    </th>
                                )}
                                {plannedActive && showPlanned && gridInputType !== 'percent' && (
                                    <th className={`headerContainer noselect`}>
                                        <Text as="div" className="headerCell day">
                                            {t('table.plannedWork')}
                                        </Text>
                                    </th>
                                )}
                                {actualActive && showPlanned && gridInputType !== 'percent' && (
                                    <th className={`headerContainer noselect`}>
                                        <Text as="div" className="headerCell day">
                                            {t('table.actualWork')}
                                        </Text>
                                    </th>
                                )}
                                {remainingActive && showPlanned && gridInputType !== 'percent' && (
                                    <th className={`headerContainer noselect`}>
                                        <Text as="div" className="headerCell day">
                                            {t('table.remainingWork')}
                                        </Text>
                                    </th>
                                )}
                                <th className="headerContainer noselect">
                                    <Text as="div" className="headerCell rowTotal">
                                        {gridInputType === 'percent' ? t('table.rowTotal.percentage') : t('table.rowTotal.nopercentage')}
                                    </Text>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {timeRowGroups.map((group: TimeRowGroup) => {
                                return (
                                    <Fragment key={group.GroupId}>
                                        <TimePeriodGroupWeek
                                            group={group}
                                            today={props.today}
                                            expanded={group.Expanded}
                                            onCellWeekUpdated={props.onCellWeekUpdated}
                                            mode={props.mode}
                                            pinMyTime={PinMyTime}
                                            deleteMyTime={DeleteMyTime}
                                            submitted={props.submitted}
                                            periodIsClosed={props.periodIsClosed}
                                            // timeLowerLimitConfig={timeEntryRoundingInterval}
                                            startDate={props.startDate}
                                            endDate={props.endDate}
                                            deadlineDate={props.deadlineDate}
                                            perioddays={perioddays}
                                            reportPeriodId={props.reportPeriodId}
                                            updateWorkType={props.updateWorkType}
                                            timesheetstate={props.timesheetstate}
                                            onRemainingWorkUpdate={props.onRemainingWorkUpdate}
                                        />
                                        <tr className="rowSpacer" />
                                        {/* {index !== timerowgroups.length - 1 && <tr className="rowSpacer" />} */}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className={`noselect totalRow`}>
                                <td
                                    className="rowHeader noselect"
                                    style={
                                        workTypesEnabled
                                            ? { width: splitterbarPosition }
                                            : { width: splitterbarPosition > 800 ? splitterbarPosition - 400 : splitterbarPosition / 2 }
                                    }
                                >
                                    <Stack horizontal className="rowHeaderContainer">
                                        <div className="expander" />
                                        <Text className="groupName">{t('table.total')}</Text>
                                    </Stack>
                                </td>
                                <td
                                    hidden={!workTypesEnabled}
                                    className="cellContainer worktypeContainer noselect"
                                    style={
                                        workTypesEnabled ? { width: splitterbarPosition } : { width: splitterbarPosition > 800 ? 400 : splitterbarPosition / 2 }
                                    }
                                >
                                    <Stack horizontal className="cell noborder">
                                        <div className="expander" />
                                        <Text className="groupName">{/* Empty for work type */}</Text>
                                    </Stack>
                                </td>
                                {/* ONLY FOR NON-PERCENT VIEW */}
                                {plannedActive && showPlanned && gridInputType !== 'percent' && (
                                    <td className={`noselect`}>
                                        <div className="totalCell">
                                            <DisplayCell value={0} periodDays={perioddays} />
                                        </div>
                                    </td>
                                )}
                                {/* ONLY FOR NON-PERCENT VIEW */}
                                {actualActive && showPlanned && gridInputType !== 'percent' && (
                                    <td className={`noselect`}>
                                        <div className="totalCell">
                                            <DisplayCell value={0} periodDays={perioddays} />
                                        </div>
                                    </td>
                                )}
                                {/* ONLY FOR NON-PERCENT VIEW */}
                                {remainingActive && showPlanned && gridInputType !== 'percent' && (
                                    <td className={`noselect`}>
                                        <div className="totalCell">
                                            <DisplayCell value={0} periodDays={perioddays} />
                                        </div>
                                    </td>
                                )}
                                <td className={`noselect`}>
                                    <div className="totalCell">
                                        {totalarray && (
                                            <DisplayCell
                                                value={Number(Object.values(totalarray).reduce((acc: number, cur: number) => acc + cur, 0))}
                                                periodDays={workingDays}
                                            />
                                        )}
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </ScrollablePane>
        </div>
    );
};
